import InteractiveElement from '../common/IE_HOC'
import compose from 'recompose/compose'
import branch from 'recompose/branch'
import renderComponent from 'recompose/renderComponent'
import Trigger2D from '../../components/2d/trigger/Trigger'
import React from 'react'
import { lookupElement } from '../../methods/lookupElement'
import { openModal, forceMediaPause, setForcePause } from '../../actions/common'
import { connect } from 'react-redux'
import { triggerMouseEntered, triggerClicked, setAudioGuidePlaying } from '../../actions/interactiveElementsActions'
import { MEDIA_TYPES } from 'aria-viewer/tour/constants'
import { playAudio, stopAudio, AUDIO_TOUR, AUDIO_HS, AUDIO_PLAY } from 'aria-viewer/audiomanager/audioManagerActions'
import UriHandler from 'aria-viewer/utils/uriHandler'
import { Trigger3D } from '../../components/3d/trigger3D'
import { INTERACTIVE_ELEMENT_OPTIONS } from '../../constants'
import { setCursor } from 'aria-viewer/players/panoplayer/actions/panoramaActions'
import { CursorState } from 'aria-viewer/players/panoplayer/constants'
import { Capabilities, displayFeatureBasedOnViewportSize } from 'aria-viewer/utils/capabilities'

const withType = Component => props => <Component {...props} type="triggers" />

const addProps = Component => ({
    state,
    isUserDraggingPano,
    isPanoAutoRotating,
    visitedStop,
    camera,
    scene,
    renderer,
    id,
    idx,
    title,
    cartesian3DCoords,
    mainSceneImageResource,
    onClick,
    contextType,
    ...props
}) => {
    const mediaItem = lookupElement({ type: 'mediaItems', id: props.mediaItems[0], state, toJS: true })
    const { url: videoUrl } = lookupElement({
        type: 'videoMedia',
        id: mediaItem.videoMedia[0],
        state,
        toJS: true,
    }).renditions[0]
    let isVisited = false
    let currentState = 'default'

    // Gets device mobile status
    const isMobile = state.getIn(['device', 'isDeviceMobile'])

    const isDeviceIOS = state.getIn(['device', 'isDeviceIOS'])

    if (visitedStop) {
        isVisited = !!visitedStop.find(({ id: vId, type: vType }) => id === vId && vType === 'triggers')
    }
    if (isVisited) {
        currentState = 'visited'
    } else if (
        contextType !== INTERACTIVE_ELEMENT_OPTIONS.CONTEXT.context2D &&
        (isPanoAutoRotating || isUserDraggingPano)
    ) {
        currentState = 'panning'
    }

    return (
        <Component
            text={title}
            video={true}
            thumbnailUrl={UriHandler.appendCorsQueryParam(mediaItem.smallurl)}
            baseTexture={UriHandler.appendCorsQueryParam(videoUrl)}
            reflectionTexture={mainSceneImageResource && UriHandler.appendCorsQueryParam(mainSceneImageResource)}
            position={cartesian3DCoords}
            overAnimationTime={1000}
            outAnimationTime={1000}
            camera={camera}
            scene={scene}
            isMobile={isMobile}
            isDeviceIOS={isDeviceIOS}
            renderer={renderer}
            obj={scene}
            currentState={currentState}
            mediaItems={props.mediaItems}
            id={id}
            contextType={contextType}
            videoUrl={videoUrl}
            {...props}
        />
    )
}

const mapStateToProps = state => {
    const viewportWidth = state.getIn(['device', 'viewportWidth'])
    const viewportHeight = state.getIn(['device', 'viewportHeight'])
    const hasHover = displayFeatureBasedOnViewportSize(Capabilities.TRIGGER_HOVER, { viewportWidth, viewportHeight })
    const wasAudioPlaying = state.getIn(['interactiveElements', 'wasAudioPlaying'])
    const isAudioPlaying = state.getIn(['audioManager', 'state', AUDIO_TOUR]) == AUDIO_PLAY

    return {
        hasHover,
        wasAudioPlaying,
        isAudioPlaying,
    }
}

const mapDispatchToProps = dispatch => ({
    onHover: e => {
        dispatch(setCursor(CursorState.POINTER))
    },
    onHoverExit: e => {
        dispatch(setCursor(CursorState.GRAB))
    },
    openModal: ({ id, type, mediaId, mediaType }) => {
        dispatch(openModal({ id, type, mediaId, mediaType }))
    },
    setForcePause: pause => {
        dispatch(forceMediaPause(pause))
    },
    registerAudio: () => {
        dispatch(playAudio(AUDIO_HS))
    },
    triggerMouseEntered: () => {
        dispatch(triggerMouseEntered())
    },
    triggerClicked: () => {
        dispatch(triggerClicked())
    },
    stopTourAudio: () => {
        dispatch(stopAudio(AUDIO_TOUR))
    },
    playTourAudio: () => {
        dispatch(playAudio(AUDIO_TOUR))
    },
    setAudioGuidePlaying: wasAudioPlaying => {
        dispatch(setAudioGuidePlaying(wasAudioPlaying))
    },
})

const mergeProps = (mappedProps, actions, ownProps) => ({
    ...mappedProps,
    ...actions,
    ...ownProps,
    onClick: (e, trigger) => {
        const { openModal, triggerClicked, setForcePause, registerAudio } = actions
        const { id, mediaItems } = ownProps

        registerAudio()
        triggerClicked()
        setForcePause(true)

        openModal({
            id,
            type: 'triggers',
            mediaId: mediaItems[0],
            mediaType: MEDIA_TYPES.video360,
        })
    },
    onMouseEnter: e => {
        const { triggerMouseEntered, setForcePause, stopTourAudio, setAudioGuidePlaying } = actions
        const { isPreviewOpen, setAutoRotate } = ownProps
        const { isAudioPlaying } = mappedProps

        if (!isPreviewOpen) {
            setAutoRotate && setAutoRotate(false)
            setForcePause(true)
            triggerMouseEntered()

            if (isAudioPlaying) {
                stopTourAudio()
            }

            setAudioGuidePlaying(isAudioPlaying)
        }
    },
    onMouseLeave: e => {
        const { setForcePause, playTourAudio } = actions
        const { wasAudioPlaying } = mappedProps

        setForcePause(false)

        if (wasAudioPlaying) {
            playTourAudio()
        }
    },
})

export default compose(
    withType,
    InteractiveElement,
    addProps,
    connect(mapStateToProps, mapDispatchToProps, mergeProps),
    branch(
        ({ enable3D, contextType }) => enable3D && contextType !== INTERACTIVE_ELEMENT_OPTIONS.CONTEXT.context2D,
        renderComponent(Trigger3D),
        renderComponent(Trigger2D)
    )
)()
